import React from 'react';
import { Empty } from 'antd';
import { Player } from '@lottiefiles/react-lottie-player';
import { motion } from 'framer-motion';
import './style.scss';

export interface EmptyStateProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Action node, e.g. a primary button. */
  action?: React.ReactNode;
  /** Lottie animation source (URL or JSON object). Takes priority over `image`. */
  lottieSrc?: string | object;
  /** Custom illustration node (img/svg). Used when no Lottie provided. */
  image?: React.ReactNode;
  /** Size of the illustration area. */
  size?: 'small' | 'default' | 'large';
  className?: string;
}

const SIZE_MAP: Record<NonNullable<EmptyStateProps['size']>, number> = {
  small: 120,
  default: 180,
  large: 240,
};

/**
 * Friendly empty state with optional Lottie illustration. Presentation only.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  action,
  lottieSrc,
  image,
  size = 'default',
  className,
}) => {
  const dimension = SIZE_MAP[size];
  const rootClassName = ['ui-empty-state', className].filter(Boolean).join(' ');

  let art: React.ReactNode;
  if (lottieSrc) {
    art = <Player autoplay loop src={lottieSrc as any} style={{ width: '100%', height: '100%' }} />;
  } else if (image) {
    art = image;
  } else {
    art = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />;
  }

  return (
    <motion.div
      className={rootClassName}
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="ui-empty-state__art" style={{ width: dimension, height: dimension }}>
        {art}
      </div>
      {title ? <div className="ui-empty-state__title">{title}</div> : null}
      {description ? <div className="ui-empty-state__description">{description}</div> : null}
      {action ? <div className="ui-empty-state__action">{action}</div> : null}
    </motion.div>
  );
};

export default EmptyState;
